import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import SearchForm from './components/SearchForm';
import ResultsTable from './components/ResultsTable';
import ColumnToggles from './components/ColumnToggles';
import Stats from './components/Stats';
import Login from './components/Login';
import Register from './components/Register';
import UserProfile from './components/UserProfile';
import About from './components/AboutWrapper';
import { getToken, getAuthHeaders, isAuthenticated } from './utils/auth';

const API_BASE = '';

function App() {
  const [query, setQuery] = useState('');
  const [provider, setProvider] = useState('all');
  const [dataType, setDataType] = useState('actual');
  const [searchField, setSearchField] = useState('auto');
  const [searchType, setSearchType] = useState('certificates'); // 'certificates' or 'b2bsaas'
  const [searchMode, setSearchMode] = useState('exact'); // 'exact' or 'like' (только для certificates)
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [providers, setProviders] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({
    fingerprint: true,
    validFrom: true,
    validTo: true,
  });
  
  // Аутентификация
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [currentTab, setCurrentTab] = useState('search'); // 'search' or 'about'
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'

  const limit = 50;

  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated()) {
        try {
          const response = await fetch(`${API_BASE}/api/auth/me`, {
            headers: getAuthHeaders(),
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else {
            localStorage.removeItem('auth_token');
          }
        } catch (error) {
          console.error('Auth check error:', error);
        }
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    setProviders(['AWS', 'GCP']);
    // Статическое значение вместо запроса к API для экономии памяти
    setTotalCount(12827880);
  }, []);

  // Основная функция поиска
  const performSearch = useCallback(async (newCursor = null, searchModeOverride = null) => {
    setLoading(true);
    setCursor(newCursor);

    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
      });

      // search_mode только для certificates
      if (searchType === 'certificates') {
        const modeToUse = searchModeOverride !== null ? searchModeOverride : searchMode;
        params.append('search_mode', modeToUse);
        params.append('data_type', dataType);
        if (provider !== 'all') params.append('provider', provider);
        params.append('search_field', searchField);
      } else {
        params.append('search_field', searchField === 'auto' ? 'company_keyword' : searchField);
      }

      if (newCursor) {
        params.append('cursor', newCursor);
      }

      if (query) params.append('query', query);

      const endpoint = searchType === 'certificates' ? '/api/search' : '/api/search/b2bsaas';
      const response = await fetch(`${API_BASE}${endpoint}?${params}`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
        setTotal(data.total || 0);
        setCursor(data.next_cursor || null);
      } else if (response.status === 429) {
        alert('Request limit exceeded. Please register or login to increase the limit.');
      } else {
        console.error('Search error:', response.status);
        setResults([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query, provider, dataType, searchField, searchType, searchMode, limit]);

  // Обработка поиска
  const handleSearch = (e) => {
    e?.preventDefault();
    if (dataType === 'historical') {
      setDataType('actual');
    }
    setCursor(null);
    setResults([]);
    setTotal(0);
    performSearch(null);
  };

  useEffect(() => {
    setResults([]);
    setTotal(0);
    setCursor(null);
  }, [searchType]);

  const handleNextPage = () => {
    if (cursor) {
      performSearch(cursor);
    }
  };

  const handlePrevPage = () => {
    setCursor(null);
    setResults([]);
    setTotal(0);
    performSearch(null);
  };

  const handleAuthSuccess = async () => {
    setShowLogin(false);
    setShowRegister(false);
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('auth_token');
  };

  return (
    <div className="app">
      <div className="container">
        <div className="header">
          <div className="header-left">
            <div className="icon">🔒</div>
            <h1>Search for actual and historical data on cloud assets - AWS, GCP</h1>
          </div>
          <div className="header-right">
            {user ? (
              <>
                <span className="user-name">Hello, {user.username}!</span>
                <button className="auth-button-header" onClick={() => setShowProfile(true)}>
                  Profile
                </button>
                <button className="auth-button-header logout" onClick={handleLogout}>
                  Logout
                </button>
              </>
            ) : (
              <>
                <button className="auth-button-header" onClick={() => { setAuthMode('login'); setShowLogin(true); }}>
                  Login
                </button>
                <button className="auth-button-header" onClick={() => { setAuthMode('register'); setShowRegister(true); }}>
                  Register
                </button>
              </>
            )}
          </div>
        </div>

        {/* Навигация */}
        <div className="nav-tabs">
          <button
            className={`nav-tab ${currentTab === 'search' ? 'active' : ''}`}
            onClick={() => setCurrentTab('search')}
          >
            Search
          </button>
          <button
            className={`nav-tab ${currentTab === 'about' ? 'active' : ''}`}
            onClick={() => setCurrentTab('about')}
          >
            About
          </button>
        </div>

        {currentTab === 'about' ? (
          <About />
        ) : (
          <>
            <SearchForm
              query={query}
              setQuery={setQuery}
              provider={provider}
              setProvider={setProvider}
              providers={providers}
              dataType={dataType}
              setDataType={setDataType}
              searchField={searchField}
              setSearchField={setSearchField}
              searchType={searchType}
              setSearchType={setSearchType}
              searchMode={searchMode}
              setSearchMode={setSearchMode}
              onSearch={handleSearch}
            />

            <ColumnToggles
              visibleColumns={visibleColumns}
              setVisibleColumns={setVisibleColumns}
            />

            <Stats totalCount={totalCount} />

            {loading && (
              <div className="loading">Loading...</div>
            )}

            {!loading && results.length > 0 && (
              <ResultsTable
                results={results}
                query={query}
                total={total}
                cursor={cursor}
                limit={limit}
                visibleColumns={visibleColumns}
                searchType={searchType}
                onNextPage={handleNextPage}
                onPrevPage={handlePrevPage}
              />
            )}

            {!loading && results.length === 0 && query && (
              <div className="no-results">
                <div>No results found</div>
                {searchType === 'certificates' && searchMode === 'exact' && (
                  <div className="no-results-suggestion">
                    <p>Try searching with <strong>Partial Match (LIKE)</strong> to find similar results.</p>
                    <button 
                      className="suggestion-button"
                      onClick={() => {
                        setSearchMode('like');
                        setCursor(null);
                        setResults([]);
                        setTotal(0);
                        performSearch(null, 'like');
                      }}
                    >
                      Search with Partial Match
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {showLogin && (
          <Login
            onClose={() => setShowLogin(false)}
            onSuccess={handleAuthSuccess}
            onSwitchToRegister={() => { setShowLogin(false); setShowRegister(true); }}
          />
        )}

        {showRegister && (
          <Register
            onClose={() => setShowRegister(false)}
            onSuccess={handleAuthSuccess}
            onSwitchToLogin={() => { setShowRegister(false); setShowLogin(true); }}
          />
        )}

        {showProfile && (
          <UserProfile
            user={user}
            onClose={() => setShowProfile(false)}
            onLogout={handleLogout}
          />
        )}
      </div>
    </div>
  );
}

export default App;
